import { useEffect } from 'react';

export const SITE_URL = 'https://flamelink.site';
export const SITE_NAME = 'FlameLink';
export const OG_IMAGE = `${SITE_URL}/og/flamelink-og-1200x630.png`;

// Single source of truth for indexable public routes (also drives scripts/seo/build_sitemap.py).
export const PUBLIC_PAGES = {
  '/': { title: 'FlameLink | Online Dating for Meaningful Connections', name: 'Home',
    description: 'FlameLink is an online dating app for adults 18+. Swipe, match and chat with people in supported cities, with daily free likes and an optional Premium plan.' },
  '/cities': { title: 'Supported Cities | FlameLink', name: 'Supported Cities',
    description: 'Check whether FlameLink is available in your city. Browse the cities we currently serve and enter your ZIP or postal code to see coverage.' },
  '/premium': { title: 'FlameLink Premium Pricing & Plans', name: 'Premium Pricing',
    description: 'Compare FlameLink Premium plans — monthly, 3-month and annual — with unlimited likes and the ability to see who liked you. Cancel anytime.' },
  '/help': { title: 'Help Center | FlameLink', name: 'Help Center',
    description: 'Answers to common FlameLink questions: creating a profile, matching, messaging, Premium, safety and account settings.' },
  '/about': { title: 'About FlameLink', name: 'About Us',
    description: 'Learn what FlameLink is, who it is for and the principles behind the app: real people, safety first and respectful connections.' },
  '/safety': { title: 'Dating Safety Tips | FlameLink', name: 'Safety Tips',
    description: 'Practical safety advice for online dating and meeting matches in person, plus how to block and report on FlameLink.' },
  '/community-guidelines': { title: 'Community Guidelines | FlameLink', name: 'Community Guidelines',
    description: 'The behaviour FlameLink expects from every member, what is not allowed, and how moderation decisions are made.' },
  '/contact': { title: 'Contact & Support | FlameLink', name: 'Contact & Support',
    description: 'Contact the FlameLink support team. Send a question and receive a ticket number by email.' },
  '/report': { title: 'Report an Issue | FlameLink', name: 'Report an Issue',
    description: 'Report a bug, a safety concern or a problem with your FlameLink account.' },
  '/legal': { title: 'Legal Center | FlameLink', name: 'Legal Center',
    description: 'All FlameLink policies in one place: Terms of Service, Privacy Policy, Cookie Policy, refunds, age verification and data processing.' },
  '/terms': { title: 'Terms of Service | FlameLink', name: 'Terms of Service', description: 'The terms that govern your use of FlameLink, including eligibility (18+), acceptable use, Premium billing and account termination.' },
  '/privacy': { title: 'Privacy Policy | FlameLink', name: 'Privacy Policy', description: 'How FlameLink collects, uses, stores and protects personal data, and the choices and rights you have.' },
  '/cookies': { title: 'Cookie Policy | FlameLink', name: 'Cookie Policy', description: 'Which cookies and similar technologies FlameLink uses and how you can control them.' },
  '/refund-policy': { title: 'Payments & Refunds | FlameLink', name: 'Payments & Refunds', description: 'How FlameLink Premium billing, renewals, cancellation and refunds work.' },
  '/age-verification': { title: 'Age Verification Policy | FlameLink', name: 'Age Verification', description: 'Why FlameLink is for adults only and how age is attested and verified.' },
  '/data-processing': { title: 'Data Processing Agreement | FlameLink', name: 'Data Processing', description: 'FlameLink data processing terms, sub-processors and security commitments.' },
};

const organization = { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: SITE_NAME, url: SITE_URL,
  logo: { '@type': 'ImageObject', url: `${SITE_URL}/brand/flamelink-script-1000.png` } };

const jsonLd = (path, page) => {
  const graph = [organization, { '@type': 'WebSite', '@id': `${SITE_URL}/#website`, url: SITE_URL, name: SITE_NAME, publisher: { '@id': `${SITE_URL}/#organization` } }];
  const url = `${SITE_URL}${path === '/' ? '' : path}`;
  graph.push({ '@type': 'WebPage', '@id': `${url}#webpage`, url, name: page.title, description: page.description, isPartOf: { '@id': `${SITE_URL}/#website` }, inLanguage: 'en-US' });
  if (path !== '/') {
    graph.push({ '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: page.name, item: url }] });
  }
  return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
};

const upsert = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) { el = create(); document.head.appendChild(el); }
  el.setAttribute('data-seo', '1');
  return el;
};
const setMeta = (attr, key, content) => {
  const el = upsert(`meta[${attr}="${key}"]`, () => { const m = document.createElement('meta'); m.setAttribute(attr, key); return m; });
  el.setAttribute('content', content);
};

// Dependency-free head manager: React 19 hoists <title>/<meta> but does not de-duplicate against the static index.html tags.
export const Seo = ({ path = '/', noindex = false, title, description }) => {
  const page = PUBLIC_PAGES[path] || { title: title || SITE_NAME, description: description || PUBLIC_PAGES['/'].description, name: title || SITE_NAME };
  const t = title || page.title;
  const d = description || page.description;
  const url = `${SITE_URL}${path === '/' ? '/' : path}`;
  useEffect(() => {
    document.title = t;
    setMeta('name', 'description', d);
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:title', t);
    setMeta('property', 'og:description', d);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', OG_IMAGE);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:image:alt', 'FlameLink logo with wings on a deep ruby background');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', t);
    setMeta('name', 'twitter:description', d);
    setMeta('name', 'twitter:image', OG_IMAGE);
    const canonical = document.head.querySelector('link[rel="canonical"]');
    const robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      canonical?.remove();
      setMeta('name', 'robots', 'noindex, nofollow');
    } else {
      robots?.remove();
      upsert('link[rel="canonical"]', () => { const l = document.createElement('link'); l.setAttribute('rel', 'canonical'); return l; }).setAttribute('href', url);
    }
    document.head.querySelector('script[type="application/ld+json"][data-seo]')?.remove();
    if (!noindex && PUBLIC_PAGES[path]) {
      const sc = document.createElement('script'); sc.type = 'application/ld+json'; sc.setAttribute('data-seo', '1'); sc.textContent = jsonLd(path, page);
      document.head.appendChild(sc);
    }
  }, [path, noindex, t, d, url]);
  return null;
};

export default Seo;
